import React from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {
  isAttachedMedia,
  isEmbeddedMedia,
} from 'app/general/widgets/ImagesCarousel/utils';
import {isMediaImage} from 'app/mediaaccess/utils';
import {useAccountSettingsState} from 'app/settings/api';
import {safeCdnImageRef} from 'app/utils/strings';

import type {AttachedMediaWithChangedStateAndEmbed} from 'app/general/widgets/ImagesCarousel/types';

interface Props {
  slideId: number;
  currentSlideId: number;
  image: AttachedMediaWithChangedStateAndEmbed;
  tabIndex?: number;
  onClick: (e: React.MouseEvent) => void;
}

export const CAROUSEL_THUMBNAIL_SIZE = 64;
export const CAROUSEL_PADDING = 6;
export const CAROUSEL_CURRENT_BORDER = 3;
export const CAROUSEL_THUMBNAIL_FULL_SIZE =
  CAROUSEL_THUMBNAIL_SIZE + 2 * CAROUSEL_PADDING;

export function CarouselThumbnail({
  slideId,
  currentSlideId,
  image,
  tabIndex,
  onClick,
}: Props) {
  const {MEDIA_URL_READ} = useAccountSettingsState().data!;
  if (isAttachedMedia(image)) {
    const imageURL = isMediaImage(image)
      ? safeCdnImageRef(image.image_ref, MEDIA_URL_READ, 'small')
      : image.embed_thumb;
    return (
      <StyledThumbnail
        tabIndex={tabIndex}
        onClick={onClick}
        isCurrent={slideId === currentSlideId}
      >
        <img src={imageURL} />
      </StyledThumbnail>
    );
  } else if (isEmbeddedMedia(image)) {
    return (
      <StyledThumbnail
        tabIndex={tabIndex}
        onClick={onClick}
        isCurrent={slideId === currentSlideId}
      >
        <div className="d-flex justify-content-center py-3 px-4 bg-white">
          <i className="fa fa-video" />
        </div>
      </StyledThumbnail>
    );
  }
  return null;
}

const StyledThumbnail = styled.div<{isCurrent: boolean}>`
  padding: ${({isCurrent}) =>
    isCurrent
      ? `${CAROUSEL_PADDING - CAROUSEL_CURRENT_BORDER}px`
      : `${CAROUSEL_PADDING}px`};
  border: ${({isCurrent}) =>
    isCurrent
      ? `${CAROUSEL_CURRENT_BORDER}px solid ${colors.selectedBg}`
      : null};
  max-width: ${CAROUSEL_THUMBNAIL_FULL_SIZE}px;
  max-height: ${CAROUSEL_THUMBNAIL_FULL_SIZE}px;
  > img,
  > div {
    opacity: ${({isCurrent}) => (isCurrent ? 1.0 : 0.8)};
    display: inline-block;
    max-width: ${CAROUSEL_THUMBNAIL_SIZE}px;
    max-height: ${CAROUSEL_THUMBNAIL_SIZE}px;
  }

  ${({isCurrent}) =>
    !isCurrent &&
    `&:hover {
    padding: ${CAROUSEL_PADDING - 1}px;
    border: 1px solid ${colors.selectedBg};
    > img {
      opacity: 1;
    }
  }`}
`;
